import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, TrendingUp, MessageSquare, Zap, BarChart3, Filter, Search, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AIClosingAssistant from './components/AIClosingAssistant';
import LeadScoringPanel from './components/LeadScoringPanel';
import ClosingStrategies from './components/ClosingStrategies';
import ConversationAnalytics from './components/ConversationAnalytics';
import AutomationPanel from './components/AutomationPanel';
import PerformanceMetrics from './components/PerformanceMetrics';
import PageLayout from '../../components/ui/PageLayout';
import { supabase } from '../../lib/supabase';

const AISalesCloser = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('assistant');
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      await Promise.all([
        loadLeads(),
        loadCampaigns(),
        loadMetrics()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      // Try to load from funnel_leads table
      const { data, error } = await supabase
        ?.from('funnel_leads')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('lead_score', { ascending: false })
        ?.limit(50);

      if (error) {
        console.warn('Could not load funnel leads:', error?.message);
        setLeads([]);
        return;
      }

      // Transform to expected format
      const transformedLeads = (data || [])?.map(lead => ({
        id: lead?.id,
        name: `${lead?.first_name || ''} ${lead?.last_name || ''}`?.trim() || lead?.email,
        company: lead?.company || 'Unknown',
        email: lead?.email,
        phone: lead?.phone || '',
        score: lead?.lead_score || 0,
        status: lead?.lead_score >= 80 ? 'hot' : lead?.lead_score >= 50 ? 'warm' : 'cold',
        lastContact: lead?.updated_at,
        engagementLevel: lead?.lead_score >= 80 ? 'high' : lead?.lead_score >= 50 ? 'medium' : 'low',
        potentialValue: lead?.total_value || 0,
        tags: [],
        source: lead?.source || 'Unknown'
      }));

      setLeads(transformedLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
      setLeads([]);
    }
  };

  const loadCampaigns = async () => {
    try {
      const { data, error } = await supabase
        ?.from('communication_campaigns')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (error) {
        console.warn('Could not load campaigns:', error?.message);
        setCampaigns([]);
        return;
      }

      setCampaigns(data || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaigns([]);
    }
  };

  const loadMetrics = async () => {
    try {
      // Load real metrics from database
      const [leadsResult, campaignsResult] = await Promise.all([
        supabase?.from('funnel_leads')?.select('id, lead_score, is_converted, total_value')?.eq('user_id', user?.id),
        supabase?.from('communication_campaigns')?.select('id, status')?.eq('user_id', user?.id)
      ]);

      const leadsData = leadsResult?.data || [];
      const campaignsData = campaignsResult?.data || [];

      const hotLeads = leadsData?.filter(l => (l?.lead_score || 0) >= 80)?.length || 0;
      const convertedLeads = leadsData?.filter(l => l?.is_converted)?.length || 0;
      const totalLeads = leadsData?.length || 0;
      const totalRevenue = leadsData?.reduce((sum, l) => sum + (l?.total_value || 0), 0) || 0;
      const activeCampaigns = campaignsData?.filter(c => c?.status === 'active')?.length || 0;

      setMetrics({
        totalLeads,
        hotLeads,
        closingRate: totalLeads > 0 ? ((convertedLeads / totalLeads) * 100)?.toFixed(1) : 0,
        avgDealSize: convertedLeads > 0 ? (totalRevenue / convertedLeads)?.toFixed(0) : 0,
        pipelineValue: totalRevenue,
        aiRecommendations: Math.floor(totalLeads * 0.5),
        automatedFollowups: activeCampaigns * 10,
        responseRate: 21.4
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
      setMetrics({
        totalLeads: 0,
        hotLeads: 0,
        closingRate: 0,
        avgDealSize: 0,
        pipelineValue: 0,
        aiRecommendations: 0,
        automatedFollowups: 0,
        responseRate: 0
      });
    }
  };

  const tabs = [
    { id: 'assistant', label: 'AI Assistant', icon: Brain },
    { id: 'scoring', label: 'Lead Scoring', icon: Target },
    { id: 'strategies', label: 'Closing Strategies', icon: MessageSquare },
    { id: 'analytics', label: 'Conversation Analytics', icon: BarChart3 },
    { id: 'automation', label: 'Automation', icon: Zap },
    { id: 'performance', label: 'Performance', icon: TrendingUp }
  ];

  const filteredLeads = leads?.filter(lead => {
    const matchesSearch = lead?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         lead?.company?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         lead?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || lead?.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) || [];

  if (loading) {
    return (
      <PageLayout>
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-gray-300 rounded-md w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)]?.map((_, i) => (
              <div key={i} className="h-32 bg-gray-300 rounded-lg"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-300 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Sales Closer</h1>
          <p className="text-muted-foreground">
            AI-powered sales assistance to help close deals and optimize strategies
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={() => loadData()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>
      {/* Metrics Summary */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Total Leads</p>
            <p className="text-2xl font-bold text-gray-900">{metrics?.totalLeads}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Hot Leads</p>
            <p className="text-2xl font-bold text-red-600">{metrics?.hotLeads}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Closing Rate</p>
            <p className="text-2xl font-bold text-green-600">{metrics?.closingRate}%</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Pipeline Value</p>
            <p className="text-2xl font-bold text-blue-600">${Number(metrics?.pipelineValue || 0)?.toLocaleString()}</p>
          </div>
        </div>
      )}
      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg mb-8">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab?.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search leads by name, company, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value || '')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value || 'all')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Leads</option>
            <option value="hot">Hot Leads</option>
            <option value="warm">Warm Leads</option>
            <option value="cold">Cold Leads</option>
          </select>
          <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <Filter className="h-4 w-4 mr-2" />
            More Filters
          </button>
        </div>
      </div>
      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'assistant' && (
          <AIClosingAssistant 
            leads={filteredLeads} 
            onLeadUpdate={loadLeads}
          />
        )}
        
        {activeTab === 'scoring' && (
          <LeadScoringPanel 
            leads={filteredLeads}
            onScoreUpdate={loadLeads}
          />
        )}
        
        {activeTab === 'strategies' && (
          <ClosingStrategies 
            leads={filteredLeads}
            campaigns={campaigns}
            onStrategyApply={loadCampaigns}
          />
        )}
        
        {activeTab === 'analytics' && (
          <ConversationAnalytics 
            metrics={metrics}
            leads={filteredLeads}
          />
        )}
        
        {activeTab === 'automation' && (
          <AutomationPanel 
            campaigns={campaigns}
            onCampaignUpdate={loadCampaigns}
          />
        )}
        
        {activeTab === 'performance' && (
          <PerformanceMetrics 
            metrics={metrics}
            campaigns={campaigns}
          />
        )}
      </motion.div>
    </PageLayout>
  );
};

export default AISalesCloser;